import styled from '@emotion/styled';
import { Box, CircularProgress, Grid, IconButton, Tooltip, Typography, tooltipClasses } from '@mui/material';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useAnalytics } from 'use-analytics';
import { ReactComponent as PulseInfoIcon } from '../../assets/pulse-info-icon.svg';
import {
  AnnuallyPulseSubscriptionsTypes,
  MonthlyPulseSubscriptionsTypes,
  PULSE_SUBSCRIPTION_TYPES,
  SeatsBillingMethods,
} from '../../modules/automated-insights/constants';
import { getUserCompany, getUserCustomizations, getUserProfile } from '../../modules/user/selectors';
import { Paths } from '../../routes';
import { TrackEvent, useTrackPageLoad } from '../analytics';
import ContactSupportModal from './ContactSupportModal';
import FreeTierDialog from './FreeTierDialog';
import SeatsBillingMethodToggle from './SeatsBillingMethodToggle';
import SeatsCounter from './SeatsCounter';
import SelectableSubscriptionCard from './SelectableSubscriptionCard';
import useChangeSubscription from './hooks/useChangeSubscription';
import useFetchSubscriptionTiers from './hooks/useFetchSubscriptionTiers';

const FREE_TIER_SUBSCRIPTION_ID = '7cfe621b-8f0d-4270-a845-d4237c984e65';

const ServicesTooltip = styled(({ className, maxWidth, ...props }) => <Tooltip {...props} />)(
  ({ theme, maxWidth }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: '#FCFCFC',
      color: theme.palette.text.primary,
      borderRadius: '24px',
      border: '1px solid #E0E0E0',
      maxWidth: maxWidth,
    },
  })
);

const styles = {
  fieldSubTitleGrid: {
    height: '30px',
  },
  fieldText: {
    marginTop: '-20px',
    color: 'white',
    fontSize: '13px',
    display: 'inline-flex',
    verticalAlign: 'middle',
  },
};

const SubscriptionSelection = (props) => {
  const [hoveredLine, setHoveredLine] = useState(null);
  const [numberOfSeats, setNumberOfSeats] = useState(1);
  const isFreeTrial = props.isFreeTrial;
  const isFreePlan = props.isFreePlan;

  useTrackPageLoad({ name: TrackEvent.VIEWED_PULSE_SUBSCRIPTION_PLANS });
  const { track } = useAnalytics();

  const { data: tiers, isLoading: isLoadingTiers } = useFetchSubscriptionTiers();
  const history = useHistory();

  const user = useSelector(getUserProfile);
  const companyInfo = useSelector(getUserCompany);
  const userCustomizations = useSelector(getUserCustomizations);

  const userSelectedTierId = user?.pulseSubscription?.pulseSubscriptionTierId;
  const userSelectedTier = user?.pulseSubscription?.pulseSubscriptionTier?.type;

  const [showContactSupportDialog, setShowContactSupportDialog] = useState(false);
  const [modalHeader, setModalHeader] = useState('');
  const [modalAction, setModalAction] = useState('');

  const [billingMethod, setBillingMethod] = useState(null);
  const [showFreeTierDialog, setShowFreeTierDialog] = useState(false);

  const selfServiceSubscription = useMemo(() => {
    return userCustomizations?.features?.selfServiceSubscription !== 'false';
  }, [userCustomizations]);

  const { mutate: changeSubscription } = useChangeSubscription();

  useEffect(() => {
    setBillingMethod(
      [
        PULSE_SUBSCRIPTION_TYPES.EssentialMonth,
        PULSE_SUBSCRIPTION_TYPES.PremiumMonth,
        PULSE_SUBSCRIPTION_TYPES.InnovatorMonth,
      ].includes(userSelectedTier)
        ? SeatsBillingMethods.Monthly
        : SeatsBillingMethods.Annually
    );
  }, [userSelectedTier]);

  if (_.isNil(user) || isLoadingTiers) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Redirect the user to /pulse if selfServiceSubscription = false or is undefined
  if (!_.isNil(userCustomizations) && !selfServiceSubscription) {
    history.replace({
      pathname: Paths.automatedInsights.basePath,
    });
  }

  const handleNumberOfSeatsChanged = (newNumber) => {
    const eventName =
      !newNumber || newNumber > numberOfSeats
        ? TrackEvent.PULSE_USER_PLAN_SELECTION_CLICKED_ADD_SEATS
        : TrackEvent.PULSE_USER_PLAN_SELECTION_CLICKED_REMOVE_SEATS;

    track(eventName, { previousValue: numberOfSeats, currentValue: newNumber });

    if (newNumber === '') {
      setNumberOfSeats('');
    }
    if (newNumber >= 1 && newNumber <= 20) {
      setNumberOfSeats(newNumber);
    }
    // Maximum number of seats = 20
    if (newNumber > 20) {
      setNumberOfSeats(20);
    }
  };

  const handleBillingMethodChanged = (newMethod) => {
    track(TrackEvent.PULSE_USER_PLAN_SELECTION_TOGGLED_BILLING_FREQUENCY, { newMethod });
    setBillingMethod(newMethod);
  };

  const handleClose = () => {
    track(TrackEvent.PULSE_USER_CLICKED_EXPLORE_PLANS);
    toggleShowFreeTierDialog();
  };

  const toggleShowFreeTierDialog = (open) => {
    setShowFreeTierDialog(open);
  };

  const toggleShowContactSupportDialog = (open, modalHeader, modalAction) => {
    if (open) {
      setModalHeader(modalHeader);
      setModalAction(modalAction);
    }
    setShowContactSupportDialog(open);
  };

  const approveFreeTier = (newPulseSubscriptionTierId) => {
    track(TrackEvent.PULSE_USER_PLAN_SELECTION_CHANGED_PLAN, {
      pulseSubscriptionId: user?.pulseSubscription?.id,
      oldPulseSubscriptionTierId: user?.pulseSubscription?.pulseSubscriptionTier?.id,
      newPulseSubscriptionTierId,
    });

    changeSubscription(
      {
        pulseSubscriptionId: user?.pulseSubscription?.id,
        newPulseSubscriptionTierId,
      },
      {
        onSuccess: () => {
          window.location.replace(Paths.automatedInsights.subscription);
        },
      }
    );
  };

  const mapTierToFeatures = (tiers) => {
    const mapping = {};

    tiers
      ?.filter((tier) => tier.isActive)
      .forEach((tier) => {
        mapping[tier.type] = tier.tierFeatures;
      });

    return mapping;
  };

  const getSubtitleForTier = (tier) => {
    let subTitle = '';
    if (tier.type === PULSE_SUBSCRIPTION_TYPES.Free) {
      subTitle = 'Up to 1 seat';
    } else if (tier.type === PULSE_SUBSCRIPTION_TYPES.Enterprise) {
      subTitle = "Let's talk!";
    } else if (
      [
        PULSE_SUBSCRIPTION_TYPES.EssentialAnnual,
        PULSE_SUBSCRIPTION_TYPES.PremiumAnnual,
        PULSE_SUBSCRIPTION_TYPES.InnovatorAnnual,
      ].includes(tier.type)
    ) {
      subTitle = 'Billed annually';
    }

    return subTitle;
  };

  const handleActionButtonClicked = (tier) => {
    if (tier.type === PULSE_SUBSCRIPTION_TYPES.Enterprise) {
      track(TrackEvent.PULSE_USER_PLAN_SELECTION_SELECTED_ENTERPRISE_TIER, {
        id: tier.id,
        type: tier.type,
        name: tier.name,
      });
      const url = 'https://info.wevo.ai/demo-revenue';
      window.location.replace(url);
    } else if (!isFreePlan && !isFreeTrial) {
      track(TrackEvent.PULSE_USER_PLAN_SELECTION_SELECTED_SELF_SERVICE_PAID_TIER, {
        id: tier.id,
        type: tier.type,
        name: tier.name,
      });

      toggleShowContactSupportDialog(true, 'Change subscription', 'change your subscription');
    } else if (tier.type === PULSE_SUBSCRIPTION_TYPES.Free) {
      track(TrackEvent.PULSE_USER_PLAN_SELECTION_SELECTED_FREE_TIER, {
        id: tier.id,
        type: tier.type,
        name: tier.name,
      });

      toggleShowFreeTierDialog(true);
    } else {
      track(TrackEvent.PULSE_USER_PLAN_SELECTION_SELECTED_SELF_SERVICE_PAID_TIER, {
        id: tier.id,
        type: tier.type,
        name: tier.name,
      });

      const firsName = user?.firstName;
      const lastName = user?.lastName;
      const email = user?.email;
      const company = companyInfo.name;

      const reference = `reference=${user?.publicId}`;
      const maxioComponent = `components[${tier['maxio_components'][0]['maxio_component_id']}]`;
      const seatsDetails = `[allocated_quantity]=${numberOfSeats}`;
      const userDetails = `first_name=${encodeURIComponent(firsName)}&last_name=${encodeURIComponent(
        lastName
      )}&email=${encodeURIComponent(email)}&organization=${encodeURIComponent(company)}`;

      const url = `${tier['maxio_url']}?${maxioComponent}${seatsDetails}&${reference}&${userDetails}`;
      window.location.replace(url);
    }
  };

  const tiersToFeatures = mapTierToFeatures(tiers);
  const services = [
    'Key findings and personas',
    'Recommendations',
    'Persona customization',
    'Journey analysis',
    'Scoring',
    'SSO',
    'Customer Success Manager',
    'Admin reports and access',
    'Number of Pulses (per user / month)',
  ];

  const servicesInfoContent = [
    `Key Findings: Insights into your target audience's response to your digital asset.
    Personas: Characteristics of the audience being targeted by the experience.  Customize it as you see fit! `,
    `Actionable suggestions tailored to improve your user experience based on this Pulse's results.`,
    'Modify the intial persona(s) that Pulse recommended, or create a new one to better fit your target audience, and then re-run the pulse through that lens.',
    'Overall experience analysis of a sequence of pages that are part of a flow.',
    'The Experience Quality Score (EQS) measures your digital experience, based on how Pulse predicts your intended audience would react, on three key metrics: Intuitive, Trusted, and Valuable.',
    'Single sign-on using one set of credentials.',
    'Priority support from the WEVO team.',
    'Access to usage and team reports.',
  ];

  const filteredTiers = tiers?.filter(
    (tier) =>
      tier.isActive &&
      (billingMethod === SeatsBillingMethods.Annually
        ? AnnuallyPulseSubscriptionsTypes.includes(tier.type)
        : MonthlyPulseSubscriptionsTypes.includes(tier.type))
  );

  const orderedTiers = (
    billingMethod === SeatsBillingMethods.Annually
      ? AnnuallyPulseSubscriptionsTypes
      : MonthlyPulseSubscriptionsTypes
  ).map((tierType) => filteredTiers?.find((filteredTier) => tierType === filteredTier.type));

  const getDiscountAmount = (tierType) => {
    switch (tierType) {
      case PULSE_SUBSCRIPTION_TYPES.EssentialAnnual:
        const essentialAnnualMonthPrice = tiers?.find(
          (tier) => tier.type === PULSE_SUBSCRIPTION_TYPES.EssentialMonth
        )?.rules.price;
        const essentialAnnualPrice = tiers?.find(
          (tier) => tier.type === PULSE_SUBSCRIPTION_TYPES.EssentialAnnual
        )?.rules.price;
        return (essentialAnnualMonthPrice - essentialAnnualPrice) * numberOfSeats;

      case PULSE_SUBSCRIPTION_TYPES.PremiumAnnual:
        const premiumMonthPrice = tiers?.find((tier) => tier.type === PULSE_SUBSCRIPTION_TYPES.PremiumMonth)
          ?.rules.price;
        const premiumAnnualPrice = tiers?.find((tier) => tier.type === PULSE_SUBSCRIPTION_TYPES.PremiumAnnual)
          ?.rules.price;
        return (premiumMonthPrice - premiumAnnualPrice) * numberOfSeats;

      case PULSE_SUBSCRIPTION_TYPES.InnovatorAnnual:
        const innovatorMonthPrice = tiers?.find(
          (tier) => tier.type === PULSE_SUBSCRIPTION_TYPES.InnovatorMonth
        )?.rules.price;
        const innovatorAnnualPrice = tiers?.find(
          (tier) => tier.type === PULSE_SUBSCRIPTION_TYPES.InnovatorAnnual
        )?.rules.price;
        return (innovatorMonthPrice - innovatorAnnualPrice) * numberOfSeats;

      default:
        break;
    }
  };

  return user?.pulseSubscription?.pulseSubscriptionTier?.type && selfServiceSubscription ? (
    <>
      <Grid
        container
        sx={{
          marginTop: { xs: 3, sm: 2 },
          marginBottom: 3,
          paddingLeft: 2,
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          columnGap: 2,
        }}>
        <Grid item sx={{ mt: 1 }}>
          <SeatsCounter
            numberOfSeats={numberOfSeats}
            setNumberOfSeats={handleNumberOfSeatsChanged}
            textFieldStyles={{ inputBorderColor: 'white', color: 'white', width: '64px' }}
          />
        </Grid>
        <Grid item sx={{ height: '40px', mt: 1.5 }}>
          <SeatsBillingMethodToggle
            billingMethod={billingMethod}
            setBillingMethod={handleBillingMethodChanged}
          />
        </Grid>
      </Grid>
      <Grid
        container
        spacing={4}
        alignItems="flex-end"
        sx={{ marginTop: -1, flexWrap: 'nowrap', paddingLeft: 2, overflowX: 'auto' }}>
        <Grid
          item
          sx={{
            minWidth: '300px',
            marginBottom: '80px',
          }}
          xs={2}>
          <Grid container spacing={2} alignItems="flex-end" sx={{ marginBottom: '36px' }}>
            {services.map((service, index) => {
              return (
                <Grid
                  item
                  xs={12}
                  onMouseEnter={() => setHoveredLine(index)}
                  onMouseLeave={() => setHoveredLine(null)}
                  key={index}
                  sx={{ ...styles.fieldSubTitleGrid }}>
                  <Typography variant="body2" sx={{ ...styles.fieldText }}>
                    {service}
                  </Typography>
                  {index < services.length - 1 && (
                    <ServicesTooltip
                      placement="right"
                      title={servicesInfoContent[index]}
                      PopperProps={{ sx: { p: 2 } }}>
                      <IconButton
                        aria-label="services"
                        size="small"
                        sx={{ marginTop: -2.5, width: '25px', height: '25px' }}>
                        <PulseInfoIcon />
                      </IconButton>
                    </ServicesTooltip>
                  )}
                </Grid>
              );
            })}
          </Grid>
        </Grid>

        {orderedTiers.map((tier, index) => {
          const isTierSelected = tier.id === userSelectedTierId;
          const subTitle = getSubtitleForTier(tier);
          const tierToFeatures = tiersToFeatures[tier.type];

          const includeFeatures = [
            tierToFeatures.keyFindingsAndPersonas,
            tierToFeatures.recommendations,
            tierToFeatures.personaCustomization,
            tierToFeatures.journeyAnalysis,
            tierToFeatures.scoringAndBenchmarking,
            tierToFeatures.sso,
            tierToFeatures.csm,
            tierToFeatures.adminReportsAndAccess,
          ];

          const displayTotalPrice = [
            PULSE_SUBSCRIPTION_TYPES.EssentialAnnual,
            PULSE_SUBSCRIPTION_TYPES.EssentialMonth,
            PULSE_SUBSCRIPTION_TYPES.PremiumAnnual,
            PULSE_SUBSCRIPTION_TYPES.PremiumMonth,
            PULSE_SUBSCRIPTION_TYPES.InnovatorAnnual,
            PULSE_SUBSCRIPTION_TYPES.InnovatorMonth,
          ].includes(tier.type);

          return (
            <SelectableSubscriptionCard
              key={index}
              user={user}
              tier={tier}
              title={tier.name}
              subTitle={subTitle}
              price={tier.rules.price || 0}
              totalPrice={displayTotalPrice && numberOfSeats * tier.rules.price}
              includeFeatures={includeFeatures}
              numberOfPulses={
                tier.type === PULSE_SUBSCRIPTION_TYPES.Enterprise
                  ? 'Up to 50 Pulses'
                  : `${tierToFeatures.usagePerSeat} Pulses`
              }
              hoveredLine={hoveredLine}
              setHoveredLine={setHoveredLine}
              subscriptionType={tier.type}
              numberOfSeats={numberOfSeats}
              getDiscountAmount={getDiscountAmount}
              isTierSelected={isTierSelected}
              onActionButtonClick={handleActionButtonClicked}
              isFreePlan={isFreePlan}
              isFreeTrial={isFreeTrial}
            />
          );
        })}
      </Grid>
      <FreeTierDialog
        open={showFreeTierDialog}
        approveFreeTier={() => approveFreeTier(FREE_TIER_SUBSCRIPTION_ID)}
        closeCallback={handleClose}
      />
      <ContactSupportModal
        open={showContactSupportDialog}
        closeCallback={toggleShowContactSupportDialog}
        modalHeader={modalHeader}
        modalAction={modalAction}
      />
    </>
  ) : (
    <Grid>
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 10 }}>
        <CircularProgress color="secondary" />
      </Box>
    </Grid>
  );
};

export default SubscriptionSelection;
